"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Camera,
  CheckCircle2,
  RotateCcw,
  ZoomIn,
  Upload,
  Paperclip,
  ListTodo,
  Plus,
  PenLine,
  Pencil,
  Check,
  Trash2,
  Sparkles,
  Maximize2,
  Minimize2,
  Mail,
  User,
  ArrowRight,
} from "lucide-react";
import { FeedbackFab } from "@/components/fab-stack/feedback-fab";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { feedbackPanelSizing } from "@/lib/feedback/panel-sizing";
import { fetchApi } from "@/lib/api/client";
import { useAuthStore } from "@/stores/auth-store";
import { useUIStore } from "@/stores/ui-store";
import { useCanReadFeedback } from "@/components/settings/hooks/use-feedback";
import { ScreenshotAnnotator } from "@/components/feedback/screenshot-annotator";
import { FeedbackClassificationFields } from "@/components/feedback/feedback-classification-fields";
import {
  FEEDBACK_STATUS_COLORS,
  FEEDBACK_STATUS_LABELS,
  type FeedbackStatus,
} from "@/lib/types/feedback";
import {
  FEEDBACK_USER_ID_KEY,
  FEEDBACK_USER_EMAIL_KEY,
  FEEDBACK_USER_NAME_KEY,
  FEEDBACK_SUBMITTER_CONFIRMED_KEY,
  MAX_FEEDBACK_LENGTH,
} from "@/lib/feedback/config";
import { buildNonAiFallback } from "@/lib/feedback/ai-fallback";
import { FeedbackAiFallbackNotice } from "@/components/feedback/feedback-ai-fallback-notice";
import { formatDistanceToNow } from "date-fns";
import {
  FEEDBACK_CATEGORY_SUFFIXES,
  type FeedbackCategory,
} from "@/lib/types/feedback";
import { useFabCorner, fabPositionClasses, type FabCorner } from "@/hooks/use-fab-corner";
import { useFabAutoCorner } from "@/hooks/use-fab-auto-corner";
import { Input } from "@/components/ui/input";
import { useTranslation } from "@/lib/i18n";

// ── Bug #18: local i18n fallback map ───────────────────────────────
// Wraps the ~10 most-visible strings. If the locale file ships a key
// under feedbackWidget.<key>, it takes precedence; otherwise we fall
// back to this English map so no string goes raw-untranslated.
// TODO(#18-full-i18n): port long body strings (AI analysis prompts,
// confirmation paragraphs, etc.) once the messages/*.json files have
// the matching keys.
const FEEDBACK_WIDGET_FALLBACK: Record<string, string> = {
  submitFeedback: "Submit Feedback",
  close: "Close",
  minimize: "Minimize",
  maximize: "Maximize",
  expand: "Expand",
  myTickets: "My tickets",
  myTicketsHeader: "My Tickets",
  newFeedback: "New Feedback",
  viewMyTickets: "View My Tickets",
  send: "Send",
  thankYou: "The admin team will review and take action. Thank you!",
  noTicketsYet: "No tickets yet",
  closePreview: "Close preview",
  dropScreenshotHere: "Drop screenshot here",
};

function useFeedbackWidgetT() {
  const { t } = useTranslation();
  return (key: keyof typeof FEEDBACK_WIDGET_FALLBACK): string => {
    const translated = t(`feedbackWidget.${key}`);
    // useTranslation.t() returns the key unchanged when missing, so
    // detect that and fall back to the English string.
    if (translated === `feedbackWidget.${key}`) {
      return FEEDBACK_WIDGET_FALLBACK[key];
    }
    return translated;
  };
}

type ChatStep =
  | "idle"
  | "identity"
  | "writing"
  | "capturing"
  | "summarizing"
  | "confirming"
  | "submitting"
  | "done";
type WidgetView = "chat" | "my-tickets";

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  content: string;
  type?: "text" | "screenshot" | "tldr";
  screenshotUrl?: string;
}

interface ThreadMessage {
  id: string;
  role: "admin" | "user";
  senderName: string;
  content: string;
  timestamp: string;
}

interface MyTicket {
  id: string;
  tldr: string;
  userStory?: string | null;
  acceptanceCriteria?: string[] | null;
  category: string;
  priority: string;
  status: string;
  timestamp?: string;
  createdAt?: string;
  affectedArea: string;
  adminResponse?: string;
  threadFile?: string | null;
  unreadByUser?: number;
  // Feedback Desk (2026-09-02) — the list row now carries these, so My
  // Tickets can show the last remark and the verification state inline.
  ticketNumber?: number | null;
  updatedAt?: string;
  resolvedAt?: string | null;
  resolvedByName?: string | null;
  resolutionNote?: string | null;
  verifiedAt?: string | null;
  verifiedByName?: string | null;
  lastReopenedAt?: string | null;
  lastRemark?: { kind: string; by: string; at: string; text: string } | null;
  screenshots?: Array<{ id: string; type: string; fileUrl: string | null }>;
}

function getDisplayTicketId(id: string): string {
  const parts = id.split("-");
  return `#${(parts[parts.length - 1] || id.slice(-6)).toUpperCase()}`;
}

/** "#523-BUG" when the row carries a ticket number, else the legacy uuid tail. */
function ticketRef(t: Pick<MyTicket, "id" | "ticketNumber" | "category">): string {
  if (t.ticketNumber != null) {
    const suffix =
      FEEDBACK_CATEGORY_SUFFIXES[t.category as FeedbackCategory] ?? "GEN";
    return `#${t.ticketNumber}-${suffix}`;
  }
  return getDisplayTicketId(t.id);
}

function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return formatDistanceToNow(d, { addSuffix: true });
}

/** Reads the previous visit's stamp and writes the new one. */
function bumpLastSeen(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(FEEDBACK_LAST_SEEN_KEY);
    localStorage.setItem(FEEDBACK_LAST_SEEN_KEY, String(Date.now()));
    const prev = raw ? Number(raw) : NaN;
    return Number.isFinite(prev) ? prev : null;
  } catch {
    return null;
  }
}

const FEEDBACK_LAST_SEEN_KEY = "feedback-widget-last-seen";

// Pre-auth + non-tenant surfaces where FeedbackWidget must NOT render.
// FIX (portal-manager-fix-wave-1, 2026-05-12): replaced the private prefix
// list with the shared constant in lib/routing/hidden-route-prefixes.ts —
// previously this list, ChatCompanion's list, and HeaderControls's list had
// drifted (eg. /teams-portal + /user-portal were missing from all three).
// The shared constant codifies the rule "chrome shows only in the main app,
// never in Admin Portal preview routes, User Portal, or Public Pages."
import { isHiddenChromeRoute } from "@/lib/routing/hidden-route-prefixes";

function getUserIdentity(): { email: string; name: string } | null {
  if (typeof window === "undefined") return null;
  const email = localStorage.getItem(FEEDBACK_USER_EMAIL_KEY);
  if (!email) return null;
  const name =
    localStorage.getItem(FEEDBACK_USER_NAME_KEY) || email.split("@")[0];
  return { email, name };
}

function setUserIdentity(email: string, name: string): void {
  localStorage.setItem(FEEDBACK_USER_EMAIL_KEY, email);
  localStorage.setItem(FEEDBACK_USER_NAME_KEY, name || email.split("@")[0]);
}

function getUserId(): string {
  if (typeof window === "undefined") return "anonymous";
  let userId = localStorage.getItem(FEEDBACK_USER_ID_KEY);
  if (!userId) {
    userId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(FEEDBACK_USER_ID_KEY, userId);
  }
  return userId;
}

export function FeedbackWidget() {
  const tw = useFeedbackWidgetT();
  const fab = useFabCorner();
  // The open chat panel anchors off the EFFECTIVE corner so on create/edit
  // pages it opens from bottom-left (same place the FAB lives after auto-
  // relocate), not on top of the wizard's Next/Submit button.
  const { effectiveCorner, cornerWithoutChat } = useFabAutoCorner();
  // The corner the open panel stays in, taken when it opens (null while closed). Following the live corner slid it
  // across the screen whenever the chat beside it closed or moved (Opus review of PR #47, 23 Oct).
  const [openedCorner, setOpenedCorner] = useState<FabCorner | null>(null);
  const panelCorner = openedCorner ?? effectiveCorner;
  const pathname = usePathname();
  const authUser = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Hide on pre-auth + non-tenant surfaces (login, onboarding, admin/portal,
  // player, etc.) — sourced from the shared HIDDEN_ROUTE_PREFIXES module.
  const isOnHiddenRoute = isHiddenChromeRoute(pathname ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [view, setView] = useState<WidgetView>("chat");
  const [step, setStep] = useState<ChatStep>("idle");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [feedback, setFeedback] = useState("");
  // Legacy single-slot aliases kept for the summarise/submit payloads;
  // these are derived from the screenshots[] array dynamically.
  // Do NOT add back the old useState declarations — use screenshots[] instead.
  const [tldrData, setTldrData] = useState<{
    tldr: string;
    userStory?: string;
    acceptanceCriteria?: string[];
    category: string;
    priority: string;
    affectedArea: string;
    screenshotContext?: string;
    aiGenerated: boolean;
  } | null>(null);

  // Editable versions of AI-generated fields for the confirmation step
  const [editableUserStory, setEditableUserStory] = useState("");
  const [editableAcceptanceCriteria, setEditableAcceptanceCriteria] = useState<
    string[]
  >([]);
  const [isEditingStory, setIsEditingStory] = useState(false);
  // Feedback Desk: the classification is editable in the review, seeded from
  // whatever the model (or the fallback) proposed each time a draft lands.
  const [editableCategory, setEditableCategory] = useState("");
  const [editablePriority, setEditablePriority] = useState("");
  const [editableArea, setEditableArea] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ role: string; content: string }>
  >([]);
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [isFullscreenAnnotating, setIsFullscreenAnnotating] = useState(false); // fullscreen annotation mode

  // Multi-screenshot accumulator (Fix 3)
  // Each entry: { id, url, source, capturedAt }
  // 'annotatingId' tracks which screenshot is currently being annotated
  const [screenshots, setScreenshots] = useState<
    { id: string; url: string; source: "auto" | "upload" | "capture"; capturedAt: string }[]
  >([]);
  const [annotatingScreenshotId, setAnnotatingScreenshotId] = useState<string | null>(null);

  // Capture-mode: widget minimised, floating button persists across navigation (Fix 4)
  const [isCaptureMode, setIsCaptureMode] = useState(false);

  // User identity state
  const [identityEmail, setIdentityEmail] = useState("");
  const [identityName, setIdentityName] = useState("");
  const [hasIdentity, setHasIdentity] = useState(false);

  // My Tickets state — paginated; fetchMyTickets(1) resets the list,
  // fetchMyTickets(N) appends. `hasMore` is true while another page exists.
  const MY_TICKETS_PAGE_SIZE = 10;
  const [myTickets, setMyTickets] = useState<MyTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [myTicketsPage, setMyTicketsPage] = useState(1);
  const [myTicketsHasMore, setMyTicketsHasMore] = useState(false);
  const [loadingMoreTickets, setLoadingMoreTickets] = useState(false);

  // Thread state (for expanded ticket in My Tickets)
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  // #176: the id of the ticket just submitted, shown (copyable) in the
  // confirmation so the reporter can quote a reference without hunting My Tickets.
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(null);
  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);

  // ── Feedback Desk (2026-09-02) ───────────────────────────────────────
  // The ONE assistant line the form shows — a clarifying question from the
  // model, or an error. The chat transcript is gone; `messages` stays as the
  // record of what the user typed (it becomes the ticket body).
  const [assistantNote, setAssistantNote] = useState<string | null>(null);
  // "Updated since your last visit": the previous visit's stamp, read when
  // My Tickets opens (the new stamp is written at the same moment).
  const [lastSeenAt, setLastSeenAt] = useState<number | null>(null);
  const [actingTicketId, setActingTicketId] = useState<string | null>(null);
  const [reopeningId, setReopeningId] = useState<string | null>(null);
  const [reopenReasonText, setReopenReasonText] = useState("");
  // What the server said when an inline Verify / Reopen was refused.
  const [actionError, setActionError] = useState<{ id: string; text: string } | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Derived: first auto-capture screenshot url (for summarise API compat)
  const screenshot = screenshots.find((s) => s.source === "auto" || s.source === "capture")?.url ?? null;
  // Derived: first upload screenshot url (for summarise API compat)
  const uploadedScreenshot = screenshots.find((s) => s.source === "upload")?.url ?? null;

  useEffect(() => {
    setMounted(true);
    // Pre-fill state from stored identity (if the user has already confirmed it)
    const confirmed =
      typeof window !== "undefined" &&
      !!localStorage.getItem(FEEDBACK_SUBMITTER_CONFIRMED_KEY);
    const identity = getUserIdentity();
    if (confirmed && identity) {
      setIdentityEmail(identity.email);
      setIdentityName(identity.name);
      setHasIdentity(true);
    } else if (isAuthenticated && authUser) {
      // Pre-fill fields but DON'T set hasIdentity — identity step will show pre-filled
      setIdentityEmail(authUser.email);
      setIdentityName(
        [authUser.firstName, authUser.lastName].filter(Boolean).join(" ") ||
          authUser.email.split("@")[0],
      );
    }
  }, [isAuthenticated, authUser]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Seed the editable classification from every new draft (AI or fallback),
  // and clear it when the draft is discarded — one place instead of six.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setEditableCategory(tldrData?.category ?? "");
    setEditablePriority(tldrData?.priority ?? "");
    setEditableArea(tldrData?.affectedArea ?? "");
  }, [tldrData]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const addMessage = useCallback(
    (
      role: "assistant" | "user",
      content: string,
      extras?: Partial<ChatMessage>,
    ) => {
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
          role,
          content,
          ...extras,
        },
      ]);
    },
    [],
  );

  // --- My Tickets ---

  const fetchMyTickets = useCallback(
    async (page: number = 1) => {
      const isFirstPage = page === 1;
      if (isFirstPage) {
        setLoadingTickets(true);
      } else {
        setLoadingMoreTickets(true);
      }
      try {
        // The BE validates submittedById as a UUID — use the real
        // authenticated user's id.
        const submittedById = authUser?.id;
        if (!submittedById) {
          if (isFirstPage) setMyTickets([]);
          setMyTicketsHasMore(false);
          return;
        }
        const url =
          `/feedback?submittedById=${submittedById}` +
          `&page=${page}&limit=${MY_TICKETS_PAGE_SIZE}`;
        const raw = await fetchApi<
          MyTicket[] | { data: MyTicket[]; total?: number; totalPages?: number }
        >(url);
        // Production returns paginated { data, total, totalPages, ... },
        // demo (legacy mock) returns a flat array.
        const tickets = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as { data: MyTicket[] }).data)
            ? (raw as { data: MyTicket[] }).data
            : [];
        const totalPages =
          !Array.isArray(raw) && typeof raw.totalPages === "number"
            ? raw.totalPages
            : undefined;

        if (isFirstPage) {
          setMyTickets(tickets);
        } else {
          setMyTickets((prev) => {
            // Dedupe by id in case the BE shifts pages between fetches.
            const seen = new Set(prev.map((t) => t.id));
            const merged = [...prev];
            for (const t of tickets) if (!seen.has(t.id)) merged.push(t);
            return merged;
          });
        }

        // hasMore: prefer BE totalPages signal; fall back to "exact page-size"
        // heuristic for the legacy demo flat-array shape.
        const hasMore =
          totalPages !== undefined
            ? page < totalPages
            : tickets.length === MY_TICKETS_PAGE_SIZE;
        setMyTicketsHasMore(hasMore);
        setMyTicketsPage(page);
      } catch {
        if (isFirstPage) setMyTickets([]);
        setMyTicketsHasMore(false);
      } finally {
        if (isFirstPage) {
          setLoadingTickets(false);
        } else {
          setLoadingMoreTickets(false);
        }
      }
    },
    [authUser],
  );

  // --- Thread functions ---

  const fetchThread = useCallback(async (ticketId: string) => {
    setThreadLoading(true);
    setThreadMessages([]);
    try {
      // GET /feedback/:ticketId/thread — returns ThreadMessage[]
      const data = await fetchApi<ThreadMessage[]>(
        `/feedback/${ticketId}/thread`,
      );
      setThreadMessages(Array.isArray(data) ? data : []);
    } catch {
      setThreadMessages([]);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  const handleSendReply = useCallback(
    async (ticketId: string) => {
      if (!replyText.trim()) return;
      setIsSendingReply(true);
      try {
        // POST /feedback/:ticketId/thread — body { message }
        // Backend determines sender from JWT (submitter = User, else = Admin)
        await fetchApi(`/feedback/${ticketId}/thread`, {
          method: "POST",
          body: JSON.stringify({ message: replyText.trim() }),
        });
        // Re-fetch thread to get updated messages
        const updated = await fetchApi<ThreadMessage[]>(
          `/feedback/${ticketId}/thread`,
        );
        setThreadMessages(Array.isArray(updated) ? updated : []);
        setReplyText("");
      } catch {
        // silent fail
      } finally {
        setIsSendingReply(false);
      }
    },
    [replyText],
  );

  // --- Feedback Desk: My Tickets actions ---

  /** Open My Tickets, remembering when it was last looked at. */
  const openMyTickets = useCallback(() => {
    setLastSeenAt(bumpLastSeen());
    setReopeningId(null);
    setReopenReasonText("");
    setView("my-tickets");
    fetchMyTickets();
  }, [fetchMyTickets]);

  /** Reporter sign-off — POST /feedback/:id/verify, then refresh the list. */
  const handleVerifyTicket = useCallback(
    async (ticketId: string) => {
      setActingTicketId(ticketId);
      setActionError(null);
      try {
        await fetchApi(`/feedback/${ticketId}/verify`, { method: "POST" });
      } catch (err) {
        // Say why (e.g. the team moved it back to In Progress meanwhile),
        // and refresh below so the row shows the server's current state.
        setActionError({
          id: ticketId,
          text: err instanceof Error && err.message ? err.message : "Could not verify — try again.",
        });
      } finally {
        await fetchMyTickets(1);
        setActingTicketId(null);
      }
    },
    [fetchMyTickets],
  );

  /** Send a resolved ticket back with a reason (the API needs >= 5 chars). */
  const handleReopenTicket = useCallback(
    async (ticketId: string) => {
      const reason = reopenReasonText.trim();
      if (reason.length < 5) return;
      setActingTicketId(ticketId);
      setActionError(null);
      try {
        await fetchApi(`/feedback/${ticketId}/reopen`, {
          method: "POST",
          body: JSON.stringify({ reason }),
        });
        setReopeningId(null);
        setReopenReasonText("");
      } catch (err) {
        // Keep the reason in the box so the reporter can retry, and say why.
        setActionError({
          id: ticketId,
          text: err instanceof Error && err.message ? err.message : "Could not reopen — try again.",
        });
      } finally {
        await fetchMyTickets(1);
        setActingTicketId(null);
      }
    },
    [reopenReasonText, fetchMyTickets],
  );

  // --- Screenshot Capture ---

  const captureScreenshot = useCallback(async (): Promise<string | null> => {
    setIsCapturing(true);
    try {
      // Wait for drawer/panel/modal animations to fully complete before capturing.
      // Uses a two-phase approach: first wait for any active CSS transitions to settle,
      // then add a safety buffer for complex staggered animations (drawers, sidepanels).
      await new Promise<void>((resolve) => {
        const animatingEl = document.querySelector(
          '[data-state="open"][class*="transition"], [data-state="open"][class*="animate"], .sheet-content, [role="dialog"]'
        );
        if (animatingEl) {
          const onEnd = () => {
            animatingEl.removeEventListener("transitionend", onEnd);
            setTimeout(resolve, 150);
          };
          animatingEl.addEventListener("transitionend", onEnd, { once: true });
          // Fallback if transitionend never fires (e.g. no active transition)
          setTimeout(() => {
            animatingEl.removeEventListener("transitionend", onEnd);
            resolve();
          }, 600);
        } else {
          // No animating element found — use a flat delay
          requestAnimationFrame(() => setTimeout(resolve, 500));
        }
      });
      const html2canvas = (await import("html2canvas-pro")).default;
      const canvas = await html2canvas(document.documentElement, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
        logging: false,
        ignoreElements: (el: Element) => el.id === "feedback-widget-container",
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
        onclone: (_doc: Document) => {
          // Strip CSS animations so html2canvas reads final-state styles instead of
          // animation-start values. Without this, Radix UI portals (sheets, drawers)
          // with slide-in/fade-in animations render off-screen or invisible.
          const style = _doc.createElement("style");
          style.textContent = "*, *::before, *::after { animation: none !important; transition: none !important; }";
          _doc.head.appendChild(style);
        },
      });

      const dataUrl = canvas.toDataURL("image/png", 0.8);
      setScreenshots((prev) => [
        ...prev,
        {
          id: `ss-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          url: dataUrl,
          source: "auto",
          capturedAt: new Date().toISOString(),
        },
      ]);
      return dataUrl;
    } catch (err) {
      console.error("Screenshot capture failed:", err);
      return null;
    } finally {
      setIsCapturing(false);
    }
  }, []);

  // --- Image Upload Helpers ---

  const processImageFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > 5 * 1024 * 1024) {
        setAssistantNote(
          "That image is too large (max 5MB). Please use a smaller screenshot.",
        );
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        if (dataUrl) {
          const newId = `ss-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setScreenshots((prev) => [
            ...prev,
            { id: newId, url: dataUrl, source: "upload", capturedAt: new Date().toISOString() },
          ]);
          // No synthetic user line: `messages` (role user) becomes the ticket
          // body, and "Uploaded a screenshot" is not something the reporter said.
          addMessage("assistant", "Got your uploaded screenshot:", {
            type: "screenshot",
            screenshotUrl: dataUrl,
          });
          addMessage("assistant", "You can continue describing your feedback.");
        }
      };
      reader.readAsDataURL(file);
    },
    [addMessage],
  );

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processImageFile(file);
      if (e.target) e.target.value = "";
    },
    [processImageFile],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) processImageFile(file);
          return;
        }
      }
    },
    [processImageFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith("image/")) {
        processImageFile(file);
      }
    },
    [processImageFile],
  );

  // --- Screenshot Annotation ---

  const handleAnnotationDone = useCallback(
    (annotatedUrl: string) => {
      // Replace the URL in the screenshots array for the screenshot being annotated
      if (annotatingScreenshotId) {
        setScreenshots((prev) =>
          prev.map((s) =>
            s.id === annotatingScreenshotId ? { ...s, url: annotatedUrl } : s,
          ),
        );
      }
      setIsAnnotating(false);
      setIsFullscreenAnnotating(false);
      setPreviewImage(null);
      setAnnotatingScreenshotId(null);
      addMessage(
        "assistant",
        "Screenshot annotated! Here's the updated version:",
        { type: "screenshot", screenshotUrl: annotatedUrl },
      );
      addMessage("assistant", "Please continue with your feedback.");
    },
    [addMessage, annotatingScreenshotId],
  );

  const handleFullscreenAnnotationDone = useCallback(
    (annotatedUrl: string) => {
      // Replace the URL in the screenshots array for the screenshot being annotated
      if (annotatingScreenshotId) {
        setScreenshots((prev) =>
          prev.map((s) =>
            s.id === annotatingScreenshotId ? { ...s, url: annotatedUrl } : s,
          ),
        );
      }
      setIsFullscreenAnnotating(false);
      setPreviewImage(null);
      setAnnotatingScreenshotId(null);
      addMessage(
        "assistant",
        "Screenshot annotated in full view! Here's the updated version:",
        { type: "screenshot", screenshotUrl: annotatedUrl },
      );
      addMessage("assistant", "Please continue with your feedback.");
    },
    [addMessage, annotatingScreenshotId],
  );

  // --- Widget Open/Close ---

  const startChatFlow = useCallback(async () => {
    setStep("capturing");
    addMessage("assistant", "Let me take a snapshot of what you're seeing...");

    setTimeout(async () => {
      const shot = await captureScreenshot();
      if (shot) {
        addMessage("assistant", "Got it! Here's what I captured:", {
          type: "screenshot",
          screenshotUrl: shot,
        });
      } else {
        // #174: captureScreenshot() swallows html2canvas failures and returns
        // null. Without this else the "Let me take a snapshot…" bubble stood
        // with no image and the ticket was filed with none, disclosed only as
        // "No screenshot was provided" deep in the review card. Say so plainly
        // and point at the paste/drag-drop path (offered in the next bubble).
        addMessage(
          "assistant",
          "I couldn't capture your screen automatically. You can paste or drag-drop a screenshot below, or just continue without one.",
        );
        setAssistantNote(
          "The page could not be captured automatically. Paste or drop a screenshot, or continue without one.",
        );
      }
      addMessage(
        "assistant",
        "What's on your mind? Tell me about a feature you'd like, something to change, or an issue you've found.\n\nYou can also paste or drag-drop a screenshot.",
      );
      setStep("writing");
      setTimeout(() => textareaRef.current?.focus(), 100);
    }, 200);
  }, [addMessage, captureScreenshot]);

  /**
   * #59 — reset every piece of a submitted conversation.
   *
   * "New Feedback" used to do `if (step === "idle") handleOpen()`. After a
   * submit, `step` is terminal, never "idle", so the ONLY reset was skipped and
   * the view flipped back to the conversation the user had just sent — which
   * has no composer, so there was no way to write a second ticket. Closing and
   * reopening worked around it precisely because handleClose sets step="idle".
   *
   * Extracted so the reset cannot be conditional again, and so open / close /
   * new-feedback cannot drift apart field by field.
   */
  const resetComposer = useCallback(() => {
    setStep("idle");
    setIsMaximized(false);
    setIsCaptureMode(false);
    setAssistantNote(null);
    setMessages([]);
    setFeedback("");
    setScreenshots([]);
    setAnnotatingScreenshotId(null);
    setTldrData(null);
    setConversationHistory([]);
    setIsAnnotating(false);
    setIsFullscreenAnnotating(false);
    setEditableUserStory("");
    setEditableAcceptanceCriteria([]);
    setIsEditingStory(false);
    setExpandedTicketId(null);
    setSubmittedTicketId(null);
  }, []);

  const handleIdentitySubmit = useCallback(() => {
    if (!identityEmail.trim()) return;
    const name = identityName.trim() || identityEmail.split("@")[0];
    setUserIdentity(identityEmail.trim(), name);
    setIdentityName(name);
    setHasIdentity(true);
    // Mark that this user has explicitly confirmed who they are
    localStorage.setItem(FEEDBACK_SUBMITTER_CONFIRMED_KEY, "1");
    startChatFlow();
  }, [identityEmail, identityName, startChatFlow]);

  const handleEditIdentity = useCallback(() => {
    // Clear confirmed flag so the identity screen shows again
    localStorage.removeItem(FEEDBACK_SUBMITTER_CONFIRMED_KEY);
    resetComposer();
    setHasIdentity(false);
    setStep("identity");
    setView("chat");
  }, [resetComposer]);



  const handleOpen = useCallback(async () => {
    // Only the maximized AI chat is closed: it covers the page. The docked
    // drawer and the floating window stay open beside the panel (the FAB has
    // stepped to a corner they are not in), so feedback can be filed about
    // the answer on screen (2026-10-21).
    const closesChat = useUIStore.getState().aiChatPanel?.mode === "maximized";
    if (closesChat) {
      window.dispatchEvent(new CustomEvent("widget:close-chat"));
    }
    // With the maximized window closing, the panel opens where the launcher goes once it is gone.
    setOpenedCorner(closesChat ? cornerWithoutChat : effectiveCorner);
    // Tell the FAB stack to hide while this panel is visible.
    window.dispatchEvent(new CustomEvent("fab-widget:open", { detail: "feedback" }));
    // ONE reset (see resetComposer) — open, close and edit-identity used to
    // carry their own copies of this list, and they had drifted (2026-09-10 review).
    resetComposer();
    setIsOpen(true);
    setView("chat");

    // Only skip identity screen if the user has explicitly confirmed their identity before
    const confirmed =
      typeof window !== "undefined" &&
      !!localStorage.getItem(FEEDBACK_SUBMITTER_CONFIRMED_KEY);
    const identity = getUserIdentity();
    if (confirmed && identity) {
      setIdentityEmail(identity.email);
      setIdentityName(identity.name);
      setHasIdentity(true);
      startChatFlow();
    } else {
      // Show identity step — pre-fill from stored identity or auth store
      if (identity) {
        setIdentityEmail(identity.email);
        setIdentityName(identity.name);
      } else if (isAuthenticated && authUser) {
        setIdentityEmail(authUser.email);
        setIdentityName(
          [authUser.firstName, authUser.lastName].filter(Boolean).join(" ") ||
            authUser.email.split("@")[0],
        );
      }
      setStep("identity");
    }
  }, [startChatFlow, isAuthenticated, authUser, resetComposer, cornerWithoutChat, effectiveCorner]);

  // Poll for unread messages every 30s (only when widget is closed and user is
  // authenticated). GET /feedback checks read:Feedback — the company tier's —
  // so the poll runs only on a KNOWN yes; for any other role it was a 403
  // twice a minute (role review, 2026-10-05). Filing feedback is unaffected.
  const canReadFeedback = useCanReadFeedback();
  useEffect(() => {
    if (isOpen || !isAuthenticated || !authUser?.id || !canReadFeedback) return;

    let cancelled = false;
    const poll = async () => {
      try {
        // BE expects a UUID submittedById — use the authenticated user's id.
        const submittedById = authUser.id;
        if (!submittedById) return;
        const raw = await fetchApi<MyTicket[] | { data: MyTicket[] }>(
          `/feedback?submittedById=${submittedById}&limit=50`,
        );
        if (cancelled) return;
        const tickets = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as { data: MyTicket[] }).data)
            ? (raw as { data: MyTicket[] }).data
            : [];
        const unreadCount = tickets.reduce(
          (sum, t) => sum + (t.unreadByUser ?? 0),
          0,
        );
        window.dispatchEvent(
          new CustomEvent("feedback:unread-count", { detail: unreadCount }),
        );
      } catch {
        // Silently ignore polling errors
      }
    };

    poll(); // Initial check
    const interval = setInterval(poll, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOpen, isAuthenticated, authUser?.id, canReadFeedback]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setOpenedCorner(null);
    resetComposer();
    // Re-show the FAB stack once the panel is gone.
    window.dispatchEvent(new CustomEvent("fab-widget:close", { detail: "feedback" }));
  }, [resetComposer]);

  // Close feedback when AI chat opens (cross-widget coordination)
  useEffect(() => {
    const handler = () => {
      if (isOpen) handleClose();
    };
    window.addEventListener("widget:close-feedback", handler);
    return () => window.removeEventListener("widget:close-feedback", handler);
  }, [isOpen, handleClose]);

  // Close on Escape key (capture phase so it fires before any stopPropagation)
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (previewImage) {
          setPreviewImage(null);
        } else if (isAnnotating) {
          setIsAnnotating(false);
        } else {
          handleClose();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen, handleClose, previewImage, isAnnotating]);

  // --- Multi-turn AI Chat ---

  const handleSendFeedback = useCallback(async () => {
    if (!feedback.trim()) return;

    const userFeedback = feedback.trim();
    setFeedback("");
    setAssistantNote(null);
    addMessage("user", userFeedback);

    const updatedHistory = [
      ...conversationHistory,
      { role: "user", content: userFeedback },
    ];
    setConversationHistory(updatedHistory);

    setStep("summarizing");
    addMessage("assistant", "Let me understand what you're saying...");

    try {
      const data = await fetchApi<{
        type?: "clarification";
        question?: string;
        tldr?: string;
        userStory?: string;
        acceptanceCriteria?: string[];
        category?: string;
        priority?: string;
        affectedArea?: string;
        screenshotContext?: string;
        aiGenerated?: boolean;
      }>("/feedback/summarize", {
        method: "POST",
        body: JSON.stringify({
          feedback: userFeedback,
          pathname: window.location.pathname,
          conversationHistory: updatedHistory,
          // `screenshot` + `uploadedScreenshot` remain for back-compat
          // (older FE deploys, custom integrations) — they are the first
          // auto-capture and the first upload. `additionalScreenshots`
          // carries every OTHER attached image so the LLM can consider
          // the full visual story (e.g. the user captured 3 pages via
          // "Add another screenshot from another page" — pre-fix only
          // the first reached the model). BE de-dupes + caps server-side.
          screenshot: screenshot,
          uploadedScreenshot: uploadedScreenshot,
          additionalScreenshots: screenshots
            .map((s) => s.url)
            .filter(
              (url) => url && url !== screenshot && url !== uploadedScreenshot,
            ),
        }),
      });

      // AI wants to ask a clarifying question
      if (data.type === "clarification") {
        const aiQuestion = data.question ?? "";
        setConversationHistory((prev) => [
          ...prev,
          { role: "assistant", content: aiQuestion },
        ]);
        addMessage("assistant", aiQuestion);
        // Shown inline above the text box — the one time the model speaks.
        setAssistantNote(aiQuestion);
        setStep("writing");
        setTimeout(() => textareaRef.current?.focus(), 100);
        return;
      }

      // AI produced a summary with user story
      setTldrData({
        tldr: data.tldr ?? "",
        userStory: data.userStory,
        acceptanceCriteria: data.acceptanceCriteria,
        category: data.category ?? "General",
        priority: data.priority ?? "Medium",
        affectedArea: data.affectedArea ?? "Unknown",
        screenshotContext: data.screenshotContext,
        aiGenerated: data.aiGenerated ?? true,
      });
      setEditableUserStory(data.userStory ?? data.tldr ?? "");
      setEditableAcceptanceCriteria(
        Array.isArray(data.acceptanceCriteria) ? data.acceptanceCriteria : [],
      );
      setIsEditingStory(false);

      addMessage(
        "assistant",
        "I've prepared a summary of your feedback. Please review and confirm below.",
        { type: "tldr" },
      );
      setStep("confirming");
    } catch {
      // GRACEFUL, and now HONEST. The fallback itself is unchanged — the
      // reporter's own words still become the summary and the ticket is still
      // filed, because a broken model must never cost somebody their bug
      // report. What changed is that it no longer happens silently:
      // `aiGenerated: false` was previously set here, submitted to the API and
      // rendered NOWHERE, so a hand-written summary and a model-written one
      // were pixel-identical in the review panel below.
      // `<FeedbackAiFallbackNotice>` now names it. See lib/feedback/ai-fallback.ts.
      setTldrData(buildNonAiFallback(userFeedback));
      addMessage(
        "assistant",
        `I'll submit your feedback as-is:\n\n"${userFeedback.slice(0, 200)}"\n\nShall I go ahead and create this ticket?`,
        { type: "tldr" },
      );
      setStep("confirming");
    }
  }, [
    feedback,
    addMessage,
    conversationHistory,
    screenshot,
    uploadedScreenshot,
    screenshots,
    // screenshot and uploadedScreenshot are derived from screenshots[]
  ]);

  // --- Ticket Submission ---

  const handleConfirm = useCallback(async () => {
    setStep("submitting");
    addMessage("assistant", "Submitting your ticket to the admin team...");

    try {
      const ticket = await fetchApi<{
        id: string;
        ticketNumber?: number | null;
        category?: string;
      }>("/feedback", {
        method: "POST",
        body: JSON.stringify({
          feedback: messages
            .filter((m) => m.role === "user")
            .map((m) => m.content)
            .join("\n\n"),
          url: window.location.href,
          pathname: window.location.pathname,
          userAgent: navigator.userAgent,
          screenResolution: `${window.innerWidth}x${window.innerHeight}`,
          submitterName: identityName || undefined,
          submitterEmail: identityEmail || undefined,
          tldr: tldrData?.tldr,
          userStory: editableUserStory || tldrData?.userStory,
          acceptanceCriteria:
            editableAcceptanceCriteria.length > 0
              ? editableAcceptanceCriteria
              : tldrData?.acceptanceCriteria,
          // The review lets the reporter correct the model's classification.
          category: editableCategory || tldrData?.category,
          priority: editablePriority || tldrData?.priority,
          affectedArea: editableArea.trim() || tldrData?.affectedArea,
          screenshotContext: tldrData?.screenshotContext,
          aiGenerated: tldrData?.aiGenerated,
        }),
      });

      // Upload all accumulated screenshots to file storage (non-blocking)
      if (ticket?.id && screenshots.length > 0) {
        const uploads: Promise<unknown>[] = screenshots.map((ss) =>
          fetchApi(`/feedback/${ticket.id}/screenshots/upload`, {
            method: "POST",
            body: JSON.stringify({
              dataUrl: ss.url,
              type: ss.source === "upload" ? "Uploaded" : "Auto",
            }),
          }).catch(() => {
            /* silent — screenshot upload is best-effort */
          }),
        );
        await Promise.all(uploads);
      }

      // Show what was submitted so the user has a record
      const submittedStory = editableUserStory || tldrData?.userStory;
      const submittedCriteria =
        editableAcceptanceCriteria.length > 0
          ? editableAcceptanceCriteria
          : tldrData?.acceptanceCriteria;

      let confirmationMsg =
        "Your ticket has been submitted! Here's what was recorded:\n\n";
      if (tldrData?.tldr) {
        confirmationMsg += `**Summary:** ${tldrData.tldr}\n\n`;
      }
      if (submittedStory) {
        confirmationMsg += `**User Story:**\n"${submittedStory}"\n\n`;
      }
      if (submittedCriteria && submittedCriteria.length > 0) {
        confirmationMsg += `**Acceptance Criteria:**\n${submittedCriteria.map((c, i) => `${i + 1}. ${c}`).join("\n")}\n\n`;
      }
      confirmationMsg += tw("thankYou");

      addMessage("assistant", confirmationMsg);
      // #176: surface the reference in the confirmation — "#523-BUG" when the
      // API numbered it, else the legacy uuid tail.
      setSubmittedTicketId(
        ticketRef({
          id: ticket.id,
          ticketNumber: ticket.ticketNumber ?? null,
          category: ticket.category ?? tldrData?.category ?? "General",
        }),
      );
      setStep("done");
    } catch {
      // Stay on the review: everything the reporter approved is still on
      // screen and "Send ticket" can simply be pressed again. (Dropping to
      // the form here emptied the box and disabled Send.)
      addMessage("assistant", "Sorry, something went wrong. Please try again.");
      setAssistantNote(
        "The ticket could not be sent. Nothing was lost — press Send ticket to try again.",
      );
      setStep("confirming");
    }
  }, [
    addMessage,
    screenshots,
    tldrData,
    messages,
    editableUserStory,
    editableAcceptanceCriteria,
    // The review-step corrections. Missing from this list, handleConfirm kept
    // the closure from the render in which they were still '' and filed the
    // model's Type / Priority / Area instead of the reporter's (2026-09-10).
    editableCategory,
    editablePriority,
    editableArea,
    tw,
    identityName,
    identityEmail,
  ]);

  /**
   * "Edit description" — back to the form with everything typed so far in
   * the box, so nothing is retyped. The transcript restarts: what is sent
   * next IS the description (the ticket body is the user messages joined).
   */
  const handleReject = useCallback(() => {
    const previous = messages
      .filter((m) => m.role === "user")
      .map((m) => m.content)
      .join("\n\n");
    setFeedback((current) => current || previous);
    setMessages([]);
    setConversationHistory([]);
    setAssistantNote(null);
    setStep("writing");
    setTldrData(null);
    setEditableUserStory("");
    setEditableAcceptanceCriteria([]);
    setIsEditingStory(false);
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [messages]);

  // --- Retake Screenshot ---

  const handleRetakeScreenshot = useCallback(async () => {
    if (isCapturing) return;
    setStep("capturing");
    addMessage("assistant", "Taking a new snapshot...");

    await new Promise((resolve) => setTimeout(resolve, 300));

    const shot = await captureScreenshot();
    if (shot) {
      addMessage("assistant", "Updated! Here's the new screenshot:", {
        type: "screenshot",
        screenshotUrl: shot,
      });
    } else {
      addMessage(
        "assistant",
        "Screenshot capture failed. You can try again or upload one manually.",
      );
      // Mirror the failure into the note the review card reads, as the first
      // capture already does — a bubble alone scrolls away (2026-09-10 review).
      setAssistantNote(
        "The page could not be captured automatically. Paste or drop a screenshot, or continue without one.",
      );
    }
    addMessage("assistant", "Please continue with your feedback.");
    setStep("writing");
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [addMessage, captureScreenshot, isCapturing]);

  // Fix 4 — Enter capture mode: minimise widget, show floating button
  const handleEnterCaptureMode = useCallback(() => {
    setIsOpen(false); // visually hide the sheet but keep all state
    setIsCaptureMode(true);
  }, []);

  // Fix 4 — Capture screenshot from another page and re-open widget
  const handleCaptureModeCapture = useCallback(async () => {
    const shot = await captureScreenshot();
    if (shot) {
      // Override the auto-type entry that captureScreenshot() just pushed
      // with source='capture' so it's distinguishable
      setScreenshots((prev) => {
        const last = [...prev];
        for (let i = last.length - 1; i >= 0; i--) {
          if (last[i].source === "auto") {
            last[i] = { ...last[i], source: "capture" };
            break;
          }
        }
        return last;
      });
      addMessage("assistant", "Captured another page! Here's the new screenshot:", {
        type: "screenshot",
        screenshotUrl: shot,
      });
      addMessage("assistant", "You can capture more pages or continue with your feedback.");
    }
    setIsCaptureMode(false);
    setIsOpen(true);
  }, [captureScreenshot, addMessage]);

  const handleCancelCaptureMode = useCallback(() => {
    setIsCaptureMode(false);
    setIsOpen(true);
  }, []);

  // ── My Tickets row (Feedback Desk) ──────────────────────────────────
  // Id, title, status, verification state, the last remark, and inline
  // Verify / Reopen for a resolved ticket. Expanding still shows the thread.
  const renderMyTicketRow = (ticket: MyTicket) => {
    // #702 — the ONE status palette (lib/types/feedback.ts). This widget used
    // to read a second, local copy in lib/feedback/config.ts that disagreed
    // with it: Resolved was PURPLE here and green on the desk, while green
    // meant "Open". `ticket.status` is typed `string`, so index through a
    // fallback rather than `MAP[t].label` (the 2026-09-07 rule).
    const stColor =
      FEEDBACK_STATUS_COLORS[ticket.status as FeedbackStatus] ??
      FEEDBACK_STATUS_COLORS.Open;
    const stLabel =
      FEEDBACK_STATUS_LABELS[ticket.status as FeedbackStatus] ?? ticket.status;
    const isExpanded = expandedTicketId === ticket.id;
    const hasUnread = (ticket.unreadByUser || 0) > 0;
    const awaiting = ticket.status === "Resolved" && !ticket.verifiedAt;
    const verified = ticket.status === "Resolved" && !!ticket.verifiedAt;
    const reopened =
      !!ticket.lastReopenedAt &&
      !["Resolved", "Closed", "Wont Fix"].includes(ticket.status);
    const acting = actingTicketId === ticket.id;
    return (
      <div
        key={ticket.id}
        className="border-b last:border-b-0 transition-colors hover:bg-muted/30"
        data-testid="feedback-widget-ticket"
      >
        <button
          type="button"
          onClick={() => {
            if (isExpanded) {
              setExpandedTicketId(null);
              setThreadMessages([]);
              setReplyText("");
            } else {
              setExpandedTicketId(ticket.id);
              fetchThread(ticket.id);
            }
          }}
          className="w-full px-4 pb-1.5 pt-3 text-start"
        >
          <div className="flex items-start gap-2">
            {hasUnread && (
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
            )}
            <p
              className={cn(
                "flex-1 text-sm font-medium leading-snug",
                !isExpanded && "line-clamp-2",
              )}
            >
              {ticket.tldr}
            </p>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span dir="ltr" className="font-mono text-[10px] text-muted-foreground">
              {ticketRef(ticket)}
            </span>
            <Badge variant="outline" className={cn("h-5 text-[10px]", stColor)}>
              {stLabel}
            </Badge>
            {awaiting && (
              <span className="inline-flex h-5 items-center gap-1 rounded-full border border-orange-500/40 bg-orange-500/10 px-1.5 text-[10px] font-medium text-orange-800 dark:text-orange-300">
                Awaiting you
              </span>
            )}
            {verified && (
              <span className="inline-flex h-5 items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-1.5 text-[10px] font-medium text-emerald-800 dark:text-emerald-300">
                <CheckCircle2 className="h-3 w-3" /> You verified
              </span>
            )}
            {reopened && (
              <span className="inline-flex h-5 items-center gap-1 rounded-full border border-purple-500/40 bg-purple-500/10 px-1.5 text-[10px] font-medium text-purple-800 dark:text-purple-300">
                <RotateCcw className="h-3 w-3" /> Reopened
              </span>
            )}
            <span className="ms-auto text-[10px] text-muted-foreground">
              {fmtWhen(ticket.updatedAt ?? ticket.createdAt)}
            </span>
          </div>
        </button>

        {ticket.lastRemark && (
          <div className="px-4 pb-2">
            <div className="flex items-start gap-1.5 text-[11.5px] leading-snug">
              <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-[9px] font-semibold text-emerald-800 dark:text-emerald-300">
                {ticket.lastRemark.by.slice(0, 1).toUpperCase()}
              </span>
              <p className={cn(!isExpanded && "line-clamp-2")}>
                <span className="font-medium">{ticket.lastRemark.by}:</span>{" "}
                {ticket.lastRemark.text}
              </p>
            </div>
          </div>
        )}

        {(awaiting || verified) && (
          <div className="flex flex-wrap items-center gap-1.5 px-4 pb-3">
            {awaiting && (
              <Button
                size="sm"
                className="h-7 gap-1 px-2 text-[11px]"
                disabled={acting}
                onClick={() => handleVerifyTicket(ticket.id)}
              >
                {acting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3 w-3" />
                )}
                Verify
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 px-2 text-[11px]"
              disabled={acting}
              onClick={() => {
                setReopeningId(reopeningId === ticket.id ? null : ticket.id);
                setReopenReasonText("");
              }}
            >
              <RotateCcw className="h-3 w-3" /> Reopen
            </Button>
          </div>
        )}

        {actionError?.id === ticket.id && (
          <p className="px-4 pb-2 text-[11px] text-destructive" role="alert">
            {actionError.text}
          </p>
        )}

        {reopeningId === ticket.id && (
          <div className="space-y-1.5 px-4 pb-3">
            <Textarea
              placeholder="What still fails, and where? (at least 5 characters)"
              value={reopenReasonText}
              onChange={(e) => setReopenReasonText(e.target.value)}
              className="min-h-[56px] text-xs"
              rows={2}
              autoFocus
            />
            <div className="flex gap-1.5">
              <Button
                size="sm"
                className="h-7 px-2 text-[11px]"
                disabled={acting || reopenReasonText.trim().length < 5}
                onClick={() => handleReopenTicket(ticket.id)}
              >
                Send it back
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-[11px]"
                onClick={() => setReopeningId(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {isExpanded && (
          <div className="space-y-2.5 px-4 pb-3">
            {threadLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              </div>
            ) : threadMessages.length > 0 ? (
              <div className="max-h-[200px] space-y-2 overflow-y-auto rounded-lg border bg-muted/20 p-2.5">
                {threadMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={cn(
                      "flex",
                      msg.role === "user" ? "justify-end" : "justify-start",
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[85%] rounded-xl px-3 py-2 text-[11px] leading-relaxed",
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "border border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200",
                      )}
                    >
                      <p className="mb-0.5 text-[10px] font-medium opacity-70">
                        {msg.senderName}
                      </p>
                      {msg.content}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-3 text-center">
                <p className="text-[11px] text-muted-foreground">No messages yet</p>
                <p className="mt-0.5 text-[10px] text-muted-foreground/60">
                  Replies from the team will appear here
                </p>
              </div>
            )}
            <div className="flex items-end gap-2">
              <Textarea
                placeholder="Reply to the team…"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className="min-h-[36px] max-h-[80px] resize-none rounded-xl border bg-muted px-3 py-2 text-xs focus-visible:ring-1"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendReply(ticket.id);
                  }
                }}
              />
              <Button
                onClick={() => handleSendReply(ticket.id)}
                disabled={!replyText.trim() || isSendingReply}
                size="icon"
                className="h-8 w-8 shrink-0 rounded-full"
              >
                {isSendingReply ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!mounted) return null;

  // Don't render on pre-auth or non-tenant surfaces — mirrors ChatCompanion.
  if (isOnHiddenRoute) return null;

  return createPortal(
    <div
      id="feedback-widget-container"
      data-slot="feedback-widget"
      className={cn(
        // FIX: 1d7d4e92 / 2ee781d8 — lower from z-[9999] to z-40 so the
        // wizard footer (z-50) sits above the FAB. z-[9999] caused the
        // feedback heart to intercept clicks on the wizard Next button
        // (same bottom-right area), making the widget appear to auto-open
        // on every Next press. The heart now lives in #fab-stack-root, so
        // this box is only the panel. Once open it goes above the AI chat
        // (drawer z-50, floating window z-51/52), which it opens beside: on a
        // narrow window they overlap, and a panel under the drawer would look
        // like a launcher that did nothing (2026-10-21).
        "fixed transition-[bottom,left,right,top] duration-500 ease-[cubic-bezier(.16,1,.3,1)] motion-reduce:transition-none",
        isOpen ? "z-[60]" : "z-40",
        // rtl-codemod-ignore-next-line: fabPositionClasses returns physical corners; the closed spot is the default one
        isOpen ? fabPositionClasses(panelCorner, "primary") : "bottom-6 right-6 pointer-events-none",
      )}
      style={{ pointerEvents: isOpen ? "auto" : undefined }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Hidden file input for screenshot upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* Draggable FAB — independent trigger for feedback */}
      {!isOpen && (
        <FeedbackFab onOpen={handleOpen} unreadCount={0} />
      )}

      {/* Maximized backdrop */}
      {isOpen && isMaximized && (
        <div className="fixed inset-0 z-[9998] bg-background/80 backdrop-blur-sm" />
      )}

      {/* Chat Panel — Feedback Desk (2026-09-02). One step at a time, and the
          panel is as tall as the step it shows: no chat transcript, the review
          replaces the form, the filed card is a card. Each step's scroll region
          is `min-h-0 flex-1 overflow-y-auto`; the panel itself carries only a
          max-height, never a fixed one. */}
      {isOpen && (
        <div
          className={cn(
            "flex flex-col rounded-2xl border bg-background shadow-2xl overflow-hidden",
            "animate-in fade-in-0 zoom-in-95 duration-200",
            isMaximized
              ? "fixed inset-4 z-[9999]"
              : cn(
                  // A DEFINITE height, filling from just under the header down to the
                  // FAB. Product decision, 2026-09-07, taken with the trade-off
                  // below stated and accepted.
                  //
                  // This SUPERSEDES the 2026-09-02 "content-sized" rule, which
                  // came from the opposite complaint — a fixed 400x520 panel left
                  // "Ticket submitted!" floating in ~260px of empty box. That
                  // effect returns here, and more of it: a short step now sits in
                  // a full-height panel. It was judged the better trade against
                  // the long steps (ticket list, review, compose with
                  // screenshots) scrolling inside a 520px box while most of the
                  // window sat empty.
                  //
                  // Geometry: anchored `bottom-14` above a `bottom-6` FAB, so the
                  // bottom edge is 80px off the viewport floor; 10rem spends the
                  // same 80px at the top, landing the top at 80px — just under
                  // the 64px app header. Measured at a 900px viewport: top 80,
                  // bottom 80, height 740; at 1608px tall, height 1448.
                  //
                  // The empty-space complaint DID return (#639, 2026-09-08 —
                  // the day after this decision), so the prescribed fix is
                  // applied: per-STEP sizing, this line not reverted. Short
                  // steps size to content, long ones keep the definite height,
                  // and both share the same cap. See lib/feedback/panel-sizing.
                  "absolute w-[calc(100vw-2rem)] sm:w-[400px] max-w-[400px]",
                  feedbackPanelSizing(step, view),
                  panelCorner.startsWith("bottom")
                    ? "bottom-14 slide-in-from-bottom-2"
                    : "top-14 slide-in-from-top-2",
                  panelCorner.endsWith("right") ? "right-0" : "left-0",
                ),
          )}
          onPaste={handlePaste}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Header — slim; the page path lives here, not in a bar under the form */}
          <div className="flex items-center justify-between gap-2 border-b bg-primary px-4 py-2.5 text-primary-foreground">
            <div className="flex min-w-0 items-center gap-2">
              <MessageCircle className="h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">Feedback</h3>
                  {view === "chat" && step !== "identity" && pathname && (
                    // Left to right on every page, so a cut path keeps its start.
                    <span
                      dir="ltr"
                      className="max-w-[180px] truncate rounded bg-white/15 px-1.5 py-0.5 font-mono text-[10px] opacity-90"
                      title={pathname}
                    >
                      {pathname}
                    </span>
                  )}
                </div>
                {hasIdentity ? (
                  <button
                    onClick={handleEditIdentity}
                    title="Change your identity"
                    className="flex items-center gap-1 text-[10px] opacity-80 transition-opacity hover:opacity-100"
                  >
                    <Mail className="h-2.5 w-2.5" />
                    <span dir="ltr" className="truncate">{identityEmail}</span>
                    <Pencil className="h-2 w-2" />
                  </button>
                ) : (
                  <p className="text-[10px] opacity-80">Help us improve the platform</p>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                onClick={() => {
                  if (view === "my-tickets") {
                    setView("chat");
                  } else {
                    openMyTickets();
                  }
                }}
                className={cn(
                  "rounded-md p-1.5 transition-colors",
                  view === "my-tickets" ? "bg-white/20" : "hover:bg-white/20",
                )}
                aria-label={tw("myTickets")}
                title={tw("myTickets")}
              >
                <ListTodo className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsMaximized(!isMaximized)}
                className="rounded-md p-1.5 transition-colors hover:bg-white/20"
                aria-label={isMaximized ? tw("minimize") : tw("maximize")}
                title={isMaximized ? tw("minimize") : tw("expand")}
              >
                {isMaximized ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={handleClose}
                className="rounded-md p-1 transition-colors hover:bg-white/20"
                aria-label={tw("close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Drag overlay */}
          {isDragging && view === "chat" && (
            <div className="absolute inset-0 z-10 m-1 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary bg-primary/10">
              <div className="flex flex-col items-center gap-2 text-primary">
                <Upload className="h-8 w-8" />
                <p className="text-sm font-medium">{tw("dropScreenshotHere")}</p>
              </div>
            </div>
          )}

          {/* === MY TICKETS VIEW === */}
          {view === "my-tickets" && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex items-center justify-between border-b px-4 py-2.5">
                <h4 className="text-sm font-semibold">{tw("myTicketsHeader")}</h4>
                <button
                  onClick={() => {
                    // #175 — keep an UNSENT draft: return to it instead of
                    // resetting. #59 — otherwise reset UNCONDITIONALLY.
                    if (feedback.trim() && step !== "done") {
                      setView("chat");
                      return;
                    }
                    resetComposer();
                    setView("chat");
                    startChatFlow();
                  }}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Plus className="h-3 w-3" /> {tw("newFeedback")}
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto">
                {loadingTickets ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : myTickets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                    <ListTodo className="mb-2 h-10 w-10 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{tw("noTicketsYet")}</p>
                    <p className="mt-1 text-xs text-muted-foreground/70">
                      Your submitted feedback will appear here.
                    </p>
                  </div>
                ) : (
                  <>
                    {(() => {
                      // "Updated since your last visit" first — exactly what the
                      // reporter needs to act on — then everything else.
                      const isRecent = (t: MyTicket) =>
                        lastSeenAt !== null &&
                        new Date(t.updatedAt ?? t.createdAt ?? 0).getTime() > lastSeenAt;
                      const recent = lastSeenAt === null ? [] : myTickets.filter(isRecent);
                      const rest =
                        lastSeenAt === null ? myTickets : myTickets.filter((t) => !isRecent(t));
                      const heading = (label: string, count: number) => (
                        <div className="flex items-center justify-between px-4 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          <span>{label}</span>
                          <span>{count}</span>
                        </div>
                      );
                      return (
                        <>
                          {recent.length > 0 && heading("Updated since your last visit", recent.length)}
                          {recent.map(renderMyTicketRow)}
                          {recent.length > 0 && rest.length > 0 && heading("Earlier", rest.length)}
                          {rest.map(renderMyTicketRow)}
                        </>
                      );
                    })()}
                    {myTicketsHasMore && (
                      <div className="flex items-center justify-center py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={loadingMoreTickets}
                          onClick={() => fetchMyTickets(myTicketsPage + 1)}
                          className="h-8 text-xs"
                        >
                          {loadingMoreTickets ? (
                            <>
                              <Loader2 className="me-1.5 h-3 w-3 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            "Load more"
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* === COMPOSE / REVIEW / FILED === */}
          {view === "chat" && (
            <>
              {/* Identity Form */}
              {step === "identity" && (
                <div className="flex flex-col items-center justify-center px-6 py-8">
                  <div className="w-full max-w-[300px] space-y-5">
                    <div className="space-y-1.5 text-center">
                      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <h4 className="text-sm font-semibold">Who are you?</h4>
                      <p className="text-xs text-muted-foreground">
                        {identityEmail
                          ? "Confirm or update your details"
                          : "So we can follow up on your feedback"}
                      </p>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">
                          Email address *
                        </label>
                        <Input
                          type="email"
                          placeholder="you@company.com"
                          value={identityEmail}
                          onChange={(e) => setIdentityEmail(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleIdentitySubmit();
                          }}
                          className="h-9 text-sm"
                          autoFocus
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">
                          Display name (optional)
                        </label>
                        <Input
                          type="text"
                          placeholder={
                            identityEmail ? identityEmail.split("@")[0] : "Your name"
                          }
                          value={identityName}
                          onChange={(e) => setIdentityName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleIdentitySubmit();
                          }}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleIdentitySubmit}
                      disabled={!identityEmail.trim()}
                      className="w-full gap-2"
                      size="sm"
                    >
                      Continue <ArrowRight className="h-3.5 w-3.5 rtl-flip" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Capturing the page — a short wait, not a bubble */}
              {step === "capturing" && (
                <div className="flex items-center justify-center gap-2 px-6 py-10 text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Capturing the page…
                </div>
              )}

              {/* Compose — one text box, the attachments, the one assistant line */}
              {(step === "writing" || step === "summarizing") && (
                <>
                  <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-2 pt-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <label htmlFor="feedback-widget-text" className="text-xs font-medium">
                        What did you notice?
                      </label>
                      <span className="text-[10px] text-muted-foreground">
                        Bug, idea or change — one thing per ticket
                      </span>
                    </div>
                    {assistantNote && (
                      <div
                        className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2 text-xs leading-relaxed"
                        role="status"
                        data-testid="feedback-widget-note"
                      >
                        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-purple-500" />
                        <span className="whitespace-pre-wrap">{assistantNote}</span>
                      </div>
                    )}
                    <Textarea
                      id="feedback-widget-text"
                      ref={textareaRef}
                      placeholder={
                        assistantNote
                          ? "Your answer…"
                          : "Describe what happened, where, and what you expected."
                      }
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      disabled={step === "summarizing"}
                      className="max-h-[220px] min-h-[112px] text-sm"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          handleSendFeedback();
                        }
                      }}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      {screenshots.map((s, i) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            setAnnotatingScreenshotId(s.id);
                            setPreviewImage(s.url);
                          }}
                          className="group relative h-11 w-16 shrink-0 overflow-hidden rounded-lg border bg-muted"
                          title={s.source === "upload" ? "Uploaded screenshot" : "Page capture"}
                          aria-label={`Open screenshot ${i + 1}`}
                        >
                          <img
                            src={s.url}
                            alt=""
                            className="h-full w-full object-cover object-top"
                          />
                          <span className="absolute bottom-0.5 start-0.5 rounded bg-black/60 px-1 text-[9px] text-white">
                            {s.source === "upload" ? "upload" : "page"}
                          </span>
                          <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                            <ZoomIn className="h-4 w-4 text-white" />
                          </span>
                        </button>
                      ))}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 px-2 text-[11px]"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Paperclip className="h-3 w-3" /> Add screenshot
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 px-2 text-[11px]"
                        onClick={handleEnterCaptureMode}
                      >
                        <Camera className="h-3 w-3" /> Capture another page
                      </Button>
                      {screenshot && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 gap-1 px-2 text-[11px]"
                          onClick={handleRetakeScreenshot}
                          disabled={isCapturing}
                        >
                          <RotateCcw className="h-3 w-3" /> Retake
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 border-t px-4 py-2.5">
                    <p
                      className={cn(
                        "me-auto text-[10px] text-muted-foreground",
                        feedback.length > MAX_FEEDBACK_LENGTH && "text-destructive",
                      )}
                    >
                      {feedback.length > 0
                        ? `${feedback.length.toLocaleString()}/${MAX_FEEDBACK_LENGTH.toLocaleString()} · `
                        : ""}
                      Paste or drop an image anywhere · Ctrl+Enter to send
                    </p>
                    <Button
                      size="sm"
                      className="h-8 gap-1.5"
                      onClick={handleSendFeedback}
                      disabled={
                        !feedback.trim() ||
                        step === "summarizing" ||
                        feedback.length > MAX_FEEDBACK_LENGTH
                      }
                    >
                      {step === "summarizing" ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Understanding…
                        </>
                      ) : (
                        <>
                          <Send className="h-3.5 w-3.5" />
                          {tw("send")}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}

              {/* Review — replaces the form; the whole panel is the review */}
              {step === "confirming" && tldrData && (
                <>
                  <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
                    {/* The degradation, named. Renders nothing when AI DID write
                        the summary. See lib/feedback/ai-fallback.ts. */}
                    <FeedbackAiFallbackNotice aiGenerated={tldrData.aiGenerated} />
                    {assistantNote ? (
                      <div
                        className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs leading-relaxed"
                        role="alert"
                        data-testid="feedback-widget-review-note"
                      >
                        <span className="whitespace-pre-wrap">{assistantNote}</span>
                      </div>
                    ) : tldrData.aiGenerated ? (
                      <div className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2 text-xs leading-relaxed">
                        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-purple-500" />
                        <span>
                          <span className="font-medium">Drafted from your description.</span>{" "}
                          Edit anything, then send.
                        </span>
                      </div>
                    ) : null}

                    <div>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Summary
                      </p>
                      <p className="rounded-md bg-muted/40 px-2.5 py-2 text-sm font-medium leading-snug">
                        {tldrData.tldr}
                      </p>
                    </div>

                    {/* Classification — the model's guess, correctable before sending.
                        #691: its own component so Type / Priority / Area can never
                        overlap (see feedback-classification-fields.tsx). */}
                    <FeedbackClassificationFields
                      category={editableCategory}
                      onCategoryChange={setEditableCategory}
                      priority={editablePriority}
                      onPriorityChange={setEditablePriority}
                      area={editableArea}
                      onAreaChange={setEditableArea}
                    />

                    {/* Editable User Story */}
                    <div>
                      <div className="mb-1 flex items-center justify-between">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          User Story
                        </p>
                        <button
                          onClick={() => setIsEditingStory(!isEditingStory)}
                          className="flex items-center gap-0.5 text-[10px] text-primary hover:underline"
                        >
                          {isEditingStory ? (
                            <>
                              <Check className="h-2.5 w-2.5" /> Done
                            </>
                          ) : (
                            <>
                              <Pencil className="h-2.5 w-2.5" /> Edit
                            </>
                          )}
                        </button>
                      </div>
                      {isEditingStory ? (
                        <Textarea
                          value={editableUserStory}
                          onChange={(e) => setEditableUserStory(e.target.value)}
                          className="max-h-[120px] min-h-[60px] resize-none border-blue-200 bg-blue-50 text-xs dark:border-blue-800/50 dark:bg-blue-950/20"
                          rows={3}
                        />
                      ) : (
                        <div className="rounded-md border border-blue-200 bg-blue-50 px-2.5 py-2 text-xs italic leading-relaxed text-blue-900 dark:border-blue-800/50 dark:bg-blue-950/20 dark:text-blue-200">
                          &ldquo;{editableUserStory}&rdquo;
                        </div>
                      )}
                    </div>

                    {/* Editable Acceptance Criteria */}
                    {(editableAcceptanceCriteria.length > 0 || isEditingStory) && (
                      <div>
                        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Acceptance Criteria
                        </p>
                        <div className="space-y-1.5">
                          {editableAcceptanceCriteria.map((criterion, i) => (
                            <div key={i} className="group flex items-start gap-2">
                              <Check className="mt-0.5 h-3 w-3 shrink-0 text-green-500" />
                              {isEditingStory ? (
                                <div className="flex flex-1 items-start gap-1">
                                  <input
                                    type="text"
                                    value={criterion}
                                    onChange={(e) => {
                                      const updated = [...editableAcceptanceCriteria];
                                      updated[i] = e.target.value;
                                      setEditableAcceptanceCriteria(updated);
                                    }}
                                    className="flex-1 rounded border bg-muted/40 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary"
                                  />
                                  <button
                                    onClick={() =>
                                      setEditableAcceptanceCriteria(
                                        editableAcceptanceCriteria.filter((_, idx) => idx !== i),
                                      )
                                    }
                                    className="shrink-0 p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                                    aria-label="Remove criterion"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <p className="text-[11px] leading-relaxed text-muted-foreground">
                                  {criterion}
                                </p>
                              )}
                            </div>
                          ))}
                          {isEditingStory && (
                            <button
                              onClick={() =>
                                setEditableAcceptanceCriteria([...editableAcceptanceCriteria, ""])
                              }
                              className="ms-5 flex items-center gap-1 text-[10px] text-primary hover:underline"
                            >
                              <Plus className="h-2.5 w-2.5" /> Add criterion
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {tldrData.screenshotContext && (
                      <p className="text-[10px] italic text-muted-foreground">
                        AI observed: {tldrData.screenshotContext}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      {screenshots.length > 0
                        ? `${screenshots.length} screenshot${screenshots.length === 1 ? "" : "s"} attached · `
                        : ""}
                      captured on <span dir="ltr" className="font-mono">{pathname}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 border-t px-4 py-2.5">
                    <Button
                      onClick={handleReject}
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit description
                    </Button>
                    <Button onClick={handleConfirm} size="sm" className="ms-auto h-8 gap-1.5">
                      <Send className="h-3.5 w-3.5" />
                      Send ticket
                    </Button>
                  </div>
                </>
              )}

              {/* Sending */}
              {step === "submitting" && (
                <div className="flex items-center justify-center gap-2 px-6 py-10 text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending your ticket…
                </div>
              )}

              {/* Filed — a card the size of its content */}
              {step === "done" && (
                <div
                  className="flex flex-col items-center gap-3 px-5 pb-4 pt-6 text-center"
                  data-testid="feedback-widget-filed"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-green-500/15 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                  </span>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    {submittedTicketId && (
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(submittedTicketId)}
                        title="Copy ticket reference"
                        dir="ltr"
                        className="rounded border bg-muted/50 px-2 py-0.5 font-mono text-xs hover:bg-muted"
                      >
                        {submittedTicketId}
                      </button>
                    )}
                    <span>Ticket submitted</span>
                  </div>
                  <p className="max-w-[300px] text-xs leading-relaxed text-muted-foreground">
                    Captured <span dir="ltr" className="font-mono">{pathname}</span>
                    {screenshots.length > 0
                      ? ` and ${screenshots.length} screenshot${screenshots.length === 1 ? "" : "s"}`
                      : ""}
                    . When the team resolves it, their remark shows up under{" "}
                    {tw("myTickets").toLowerCase()}.
                  </p>
                  <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
                    <Button
                      size="sm"
                      className="h-8"
                      onClick={() => {
                        resetComposer();
                        setView("chat");
                        startChatFlow();
                      }}
                    >
                      Submit another
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={openMyTickets}>
                      {tw("viewMyTickets")}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={handleClose}>
                      {tw("close")}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Screenshot Lightbox with Fullscreen Annotation */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in-0 duration-150"
          onClick={() => {
            if (!isFullscreenAnnotating) {
              setPreviewImage(null);
              setIsFullscreenAnnotating(false);
            }
          }}
        >
          {/* Close button — z-20 keeps it above the annotator toolbar */}
          <button
            className="absolute top-4 end-4 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 transition-colors z-20"
            onClick={() => {
              setPreviewImage(null);
              setIsFullscreenAnnotating(false);
            }}
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>

          {isFullscreenAnnotating ? (
            /* Fullscreen annotation mode */
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex flex-col items-center"
            >
              <ScreenshotAnnotator
                imageUrl={previewImage}
                onDone={handleFullscreenAnnotationDone}
                onCancel={() => setIsFullscreenAnnotating(false)}
                fullscreen
              />
            </div>
          ) : (
            /* Normal preview with annotate button */
            <>
              <img
                src={previewImage}
                alt="Screenshot preview"
                className="max-w-[90vw] max-h-[80vh] rounded-lg shadow-2xl object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              {/* Annotate button below the image */}
              <div
                className="mt-4 flex items-center gap-3"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => {
                    // Set annotatingScreenshotId to the matching screenshot entry
                    const found = screenshots.find((s) => s.url === previewImage);
                    if (found) setAnnotatingScreenshotId(found.id);
                    setIsFullscreenAnnotating(true);
                  }}
                  className="flex items-center gap-2 rounded-lg bg-white/10 border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/20 transition-colors"
                >
                  <PenLine className="h-4 w-4" /> Annotate Full View
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Fix 4 — Floating "Capture this page" button in capture mode */}
      {isCaptureMode && createPortal(
        <div className="fixed bottom-6 end-6 z-[10001] flex flex-col items-end gap-2 animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
          <div className="flex items-center gap-2 rounded-2xl bg-background border shadow-2xl px-4 py-2.5">
            <Camera className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Navigate to the page you want to capture</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 shadow-lg"
              onClick={handleCancelCaptureMode}
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
            <Button
              size="sm"
              className="gap-1.5 shadow-lg"
              onClick={handleCaptureModeCapture}
              disabled={isCapturing}
            >
              {isCapturing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Camera className="h-3.5 w-3.5" />
              )}
              Capture this page
            </Button>
          </div>
        </div>,
        document.body,
      )}
    </div>,
    document.body,
  );
}
